import { useEffect, useState, type ReactNode } from 'react'

import { Button, Input, Select } from './components/Field'
import { Popover, PopoverItem } from './components/Popover'
import { LabelSwatch, TypeIcon } from './components/IssueBadges'
import {
  ISSUE_TYPES,
  PRIORITY_LABELS,
  STATUS_LABELS,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TYPE_LABELS,
  type FilterController,
} from './filters'
import { cn } from './lib/ui'
import { useLabels, useRoster } from './useWorkspace'

function TriggerButton({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean
  count: number
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition',
        active
          ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-950 dark:text-brand-300'
          : 'border-ink-300 bg-white text-ink-600 hover:bg-ink-50 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-300 dark:hover:bg-ink-800',
      )}
    >
      {children}
      {count > 0 && <span className="tabular-nums">({count})</span>}
      <svg viewBox="0 0 16 16" className="size-3 opacity-60" fill="none" aria-hidden="true">
        <path d="M4 6.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </button>
  )
}

/**
 * The filter bar shared by the board, the backlog and any issue list.
 *
 * The text box keeps its own state and pushes into the URL after a pause, so
 * typing does not re-render the whole board on every keystroke; everything else
 * writes straight through, because a checkbox toggle is one intentional change.
 */
export function FilterBar({
  slug,
  controller,
  showStatus = true,
  className,
  children,
}: {
  slug: string | null
  controller: FilterController
  showStatus?: boolean
  className?: string
  children?: ReactNode
}) {
  const { filters, activeCount, set, toggle, clear } = controller
  const { members } = useRoster(slug)
  const { data: labels = [] } = useLabels(slug)

  const [term, setTerm] = useState(filters.q)

  // Adopt an externally changed query (a cleared filter, a pasted link) without
  // fighting the user mid-keystroke.
  useEffect(() => {
    setTerm(filters.q)
  }, [filters.q])

  useEffect(() => {
    if (term === filters.q) return
    const timer = window.setTimeout(() => set({ q: term }), 250)
    return () => window.clearTimeout(timer)
  }, [term, filters.q, set])

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <div className="relative">
        <svg
          viewBox="0 0 16 16"
          className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-400"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <Input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search issues"
          aria-label="Search issues"
          className="w-48 py-1 pl-7 text-xs"
        />
      </div>

      <Popover
        label="Filter by type"
        trigger={({ toggle: open }) => (
          <TriggerButton active={filters.type.length > 0} count={filters.type.length} onClick={open}>
            Type
          </TriggerButton>
        )}
      >
        {() => (
          <>
            {ISSUE_TYPES.map((type) => (
              <PopoverItem
                key={type}
                selected={filters.type.includes(type)}
                onClick={() => toggle('type', type)}
              >
                <span className="flex items-center gap-2">
                  <TypeIcon type={type} />
                  {TYPE_LABELS[type]}
                </span>
              </PopoverItem>
            ))}
          </>
        )}
      </Popover>

      {showStatus && (
        <Popover
          label="Filter by status"
          trigger={({ toggle: open }) => (
            <TriggerButton
              active={filters.status.length > 0}
              count={filters.status.length}
              onClick={open}
            >
              Status
            </TriggerButton>
          )}
        >
          {() => (
            <>
              {TASK_STATUSES.map((status) => (
                <PopoverItem
                  key={status}
                  selected={filters.status.includes(status)}
                  onClick={() => toggle('status', status)}
                >
                  {STATUS_LABELS[status]}
                </PopoverItem>
              ))}
            </>
          )}
        </Popover>
      )}

      <Popover
        label="Filter by priority"
        trigger={({ toggle: open }) => (
          <TriggerButton
            active={filters.priority.length > 0}
            count={filters.priority.length}
            onClick={open}
          >
            Priority
          </TriggerButton>
        )}
      >
        {() => (
          <>
            {TASK_PRIORITIES.map((priority) => (
              <PopoverItem
                key={priority}
                selected={filters.priority.includes(priority)}
                onClick={() => toggle('priority', priority)}
              >
                {PRIORITY_LABELS[priority]}
              </PopoverItem>
            ))}
          </>
        )}
      </Popover>

      <Popover
        label="Filter by label"
        width="w-56"
        trigger={({ toggle: open }) => (
          <TriggerButton
            active={filters.label.length > 0}
            count={filters.label.length}
            onClick={open}
          >
            Label
          </TriggerButton>
        )}
      >
        {() =>
          labels.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-ink-500">No labels in this workspace yet.</p>
          ) : (
            <>
              {labels.map((label) => (
                <PopoverItem
                  key={label.id}
                  selected={filters.label.includes(label.id)}
                  onClick={() => toggle('label', label.id)}
                >
                  <span className="flex items-center gap-2">
                    <LabelSwatch color={label.color} />
                    <span className="truncate">{label.name}</span>
                    <span className="ml-auto text-[11px] text-ink-400">{label.issue_count}</span>
                  </span>
                </PopoverItem>
              ))}
            </>
          )
        }
      </Popover>

      <Select
        value={filters.assignee}
        onChange={(event) => set({ assignee: event.target.value })}
        aria-label="Filter by assignee"
        className="w-36 py-1 text-xs"
      >
        <option value="">Anyone</option>
        <option value="me">Assigned to me</option>
        <option value="unassigned">Unassigned</option>
        {members.map((member) => (
          <option key={member.user_id} value={String(member.user_id)}>
            {member.user?.name ?? `User ${member.user_id}`}
          </option>
        ))}
      </Select>

      {activeCount > 0 && (
        <Button variant="ghost" size="sm" onClick={clear}>
          Clear
        </Button>
      )}

      {children}
    </div>
  )
}
